/**
 * 実行速度を視覚的に表示するコンポーネント
 */
export class SpeedVisualizer {
  private container: HTMLElement
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private animationFrame: number | null = null
  
  private particles: SpeedParticle[] = []
  private currentSpeed: number = 500
  
  constructor() {
    this.container = this.createContainer()
    this.canvas = document.createElement('canvas')
    this.canvas.className = 'speed-visualizer__canvas'
    this.container.appendChild(this.canvas)
    
    const ctx = this.canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Canvas context not available')
    }
    this.ctx = ctx
    
    this.setupStyles()
    this.setupCanvas()
    this.startAnimation()
  }
  
  /**
   * コンテナを作成
   */
  private createContainer(): HTMLElement {
    const container = document.createElement('div')
    container.className = 'speed-visualizer'
    
    // 速度コントロールの隣に配置
    const speedControl = document.querySelector('.speed-control')
    if (speedControl && speedControl.parentElement) {
      speedControl.parentElement.insertBefore(container, speedControl.nextSibling)
    }
    
    return container
  }
  
  /**
   * スタイルを設定
   */
  private setupStyles(): void {
    const style = document.createElement('style')
    style.textContent = `
      .speed-visualizer {
        position: relative;
        width: 60px;
        height: 30px;
        margin-left: 1rem;
        display: inline-block;
        vertical-align: middle;
      }
      
      .speed-visualizer__canvas {
        width: 100%;
        height: 100%;
        border-radius: 15px;
        background: rgba(37, 99, 235, 0.1);
      }
      
      @media (max-width: 768px) {
        .speed-visualizer {
          display: none;
        }
      }
    `
    document.head.appendChild(style)
  }
  
  /**
   * Canvasの初期設定
   */
  private setupCanvas(): void {
    const rect = this.canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    
    this.canvas.width = rect.width * dpr
    this.canvas.height = rect.height * dpr
    this.ctx.scale(dpr, dpr)
  }
  
  /**
   * アニメーションを開始
   */
  private startAnimation(): void {
    const animate = () => {
      this.update()
      this.draw()
      this.animationFrame = requestAnimationFrame(animate)
    }
    animate()
  }
  
  /**
   * 更新処理
   */
  private update(): void {
    // パーティクルを生成
    const particleRate = this.calculateParticleRate()
    if (Math.random() < particleRate) {
      this.particles.push({
        x: 0,
        y: this.canvas.height / 2,
        vx: 2 + Math.random() * 2,
        vy: (Math.random() - 0.5) * 1,
        size: 2 + Math.random() * 2,
        opacity: 1,
        color: this.getSpeedColor(),
      })
    }
    
    // パーティクルを更新
    this.particles = this.particles.filter(particle => {
      particle.x += particle.vx * (1000 / this.currentSpeed)
      particle.y += particle.vy
      particle.opacity *= 0.98
      particle.size *= 0.99
      
      return particle.x < this.canvas.width && particle.opacity > 0.01
    })
  }
  
  /**
   * 描画処理
   */
  private draw(): void {
    const rect = this.canvas.getBoundingClientRect()
    
    // 背景をクリア
    this.ctx.clearRect(0, 0, rect.width, rect.height)
    
    // パーティクルを描画
    this.particles.forEach(particle => {
      this.ctx.save()
      this.ctx.globalAlpha = particle.opacity
      this.ctx.fillStyle = particle.color
      this.ctx.beginPath()
      this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
      this.ctx.fill()
      this.ctx.restore()
    })
    
    // 速度インジケーターラインを描画
    this.drawSpeedLine()
  }
  
  /**
   * 速度ラインを描画
   */
  private drawSpeedLine(): void {
    const rect = this.canvas.getBoundingClientRect()
    const centerY = rect.height / 2
    
    // グラデーションを作成
    const gradient = this.ctx.createLinearGradient(0, centerY, rect.width, centerY)
    gradient.addColorStop(0, this.getSpeedColor())
    gradient.addColorStop(1, 'transparent')
    
    // ラインを描画
    this.ctx.strokeStyle = gradient
    this.ctx.lineWidth = 2
    this.ctx.setLineDash([5, 5])
    this.ctx.lineDashOffset = -Date.now() / (50 / (this.currentSpeed / 100))
    
    this.ctx.beginPath()
    this.ctx.moveTo(0, centerY)
    this.ctx.lineTo(rect.width, centerY)
    this.ctx.stroke()
    
    this.ctx.setLineDash([])
  }
  
  /**
   * パーティクル生成率を計算
   */
  private calculateParticleRate(): number {
    // 速度が速いほどパーティクルが多い
    return Math.min(0.8, 0.1 + (0.7 * (1 - this.currentSpeed / 2000)))
  }
  
  /**
   * 速度に応じた色を取得
   */
  private getSpeedColor(): string {
    if (this.currentSpeed <= 100) {
      return '#ef4444' // 赤（最速）
    } else if (this.currentSpeed <= 300) {
      return '#f59e0b' // オレンジ（速い）
    } else if (this.currentSpeed <= 700) {
      return '#2563eb' // 青（通常）
    } else {
      return '#10b981' // 緑（遅い）
    }
  }
  
  /**
   * 速度を更新
   */
  updateSpeed(speed: number): void {
    this.currentSpeed = speed
  }
  
  /**
   * クリーンアップ
   */
  dispose(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
    }
    this.container.remove()
  }
}

interface SpeedParticle {
  x: number
  y: number
  vx: number
  vy: number
  size: number
  opacity: number
  color: string
}